// What the New customer form asks the duplicate check (spec 09 §3.13, §4.1's
// E2-2c annotation, §5's E2-2 row 14). Pure, so the rules that decide WHEN a
// check is sent are unit-tested without a DOM.
//
// THE THREE RULES.
//  1. A check is sent only when there is something to check: a name with at
//     least `MIN_NAME_CHARS` letters after trimming, or any tax id. One or two
//     letters score against half the book through the trigram operator and
//     spend the reader's 30-a-minute throttle on noise. A tax id is matched
//     exactly, so any typed value is worth one check; a hidden customer's tax
//     id never gives a signal (§7 narrowing 20), so this asks nothing a
//     representative may not learn.
//  2. Each field carries the bound the create form's field has
//     (`CustomerDuplicateCheckRequest` mirrors `CustomerCreateRequest`), so a
//     long paste is cut to what the create would accept instead of earning a
//     400 the panel would have to explain.
//     U+0000 is dropped for the same reason: the contract refuses it in every
//     text field, and no customer name can hold it.
//  3. A field left empty is absent from the body, never `""`, so two bodies
//     that ask the same thing are the same query key.

import type { DuplicateCheckBody } from "../../hooks/duplicate-queries";

/** Fewer name letters than this ask nothing (rule 1). */
export const MIN_NAME_CHARS = 3;

/** The pause after the last keystroke before a check is sent. Long enough
 * that a typist sends one check per word or two, short enough that the panel
 * answers before the reader reaches the next field. */
export const DUPLICATE_CHECK_DEBOUNCE_MS = 400;

/** The bounds `CustomerDuplicateCheckRequest` takes from the create form. */
const LEGAL_NAME_MAX = 300;
const DISPLAY_NAME_MAX = 200;
const TAX_ID_MAX = 40;

export interface DuplicateCheckInput {
  readonly legalName: string;
  readonly displayName: string;
  readonly taxId: string;
}

/** The body for `input`, or `undefined` when nothing is worth checking. */
export function duplicateCheckBody(input: DuplicateCheckInput): DuplicateCheckBody | undefined {
  const legal = clean(input.legalName, LEGAL_NAME_MAX);
  const display = clean(input.displayName, DISPLAY_NAME_MAX);
  const taxId = clean(input.taxId, TAX_ID_MAX);
  const body: DuplicateCheckBody = {};
  if (legal.length >= MIN_NAME_CHARS) body.legal_name = legal;
  if (display.length >= MIN_NAME_CHARS) body.display_name = display;
  if (taxId.length > 0) body.tax_id = taxId;
  return Object.keys(body).length > 0 ? body : undefined;
}

/** Trimmed, without U+0000, and cut to the field's bound (rule 2). */
function clean(value: string, max: number): string {
  return value.replaceAll("\u0000", "").trim().slice(0, max).trim();
}
